import React, { useEffect, useState } from "react";
import { Ban, Trash2 } from "lucide-react";
import { Modal, Card, CardContent, Input, Button, EmptyState, LoadingBlock, ConfirmDialog, toast } from "@/components/ds";
import { api, formatApiError, type BlockedTime } from "@/lib/api";
import { Field } from "../onboarding/_ui";
import { fmtDateTime } from "./constants";

export function BlockedTimes({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<BlockedTime[] | null>(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);

  const load = () => api.schedBlockedList().then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!start || !end) { toast.error("Pick a start and end time."); return; }
    const s = new Date(start).toISOString(); const e = new Date(end).toISOString();
    if (e <= s) { toast.error("End must be after start."); return; }
    setSaving(true);
    try { await api.schedBlockedCreate({ start_at: s, end_at: e, reason: reason || null }); toast.success("Time blocked"); setStart(""); setEnd(""); setReason(""); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const remove = async (id: string) => {
    try { await api.schedBlockedDelete(id); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Blocked times" description="Block off holidays or breaks — no appointments can be booked in these windows." testid="blocked-times">
      <div className="max-h-[72vh] space-y-5 overflow-y-auto pr-1">
        <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-2">
          <Field label="Start" required testid="field-block-start"><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="block-start" /></Field>
          <Field label="End" required testid="field-block-end"><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="block-end" /></Field>
          <div className="sm:col-span-2"><Field label="Reason" testid="field-block-reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Holiday, team offsite…" data-testid="block-reason" /></Field></div>
          <div className="sm:col-span-2 flex justify-end"><Button onClick={add} disabled={saving} data-testid="block-add">{saving ? "Saving…" : "Block this time"}</Button></div>
        </div>

        {rows === null ? <LoadingBlock rows={3} /> : rows.length === 0 ? (
          <EmptyState icon={Ban} title="No blocked times" description="Add a window above to prevent bookings during it." />
        ) : (
          <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="blocked-list">
            {rows.map((b) => (
              <div key={b.id} className="flex items-center justify-between px-4 py-3" data-testid={`blocked-row-${b.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{fmtDateTime(b.start_at)} → {fmtDateTime(b.end_at)}</p>
                  <p className="truncate text-xs text-slate-500">{b.reason ?? "No reason"}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setToDelete(b.id)} data-testid={`blocked-delete-${b.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>
            ))}
          </CardContent></Card>
        )}
      </div>
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Remove blocked time?"
        description="Bookings will be allowed in this window again." confirmLabel="Remove" destructive
        onConfirm={() => { if (toDelete) remove(toDelete); setToDelete(null); }} testid="blocked-delete-confirm" />
    </Modal>
  );
}
